<script setup lang="ts">
import { ref } from 'vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuthStore } from '@/stores/auth'
import { Eye, EyeOff } from 'lucide-vue-next'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

const username = ref<string>('')
const password = ref<string>('')
const errorMessage = ref<string>('')
const auth = useAuthStore()
const showPassword = ref(false)
/**
 * Gestisce il submit del form di login.
 */
async function doLogin(): Promise<void> {
  errorMessage.value = '' // Resetta il messaggio di errore
  try {
    // Chiama la funzione di login dello store, che ora gestisce tutto.
    await auth.login(username.value, password.value)
    // Il redirect alla home page viene gestito automaticamente dallo store in caso di successo.
  } catch (error: unknown) {
    errorMessage.value = (error as { message: string }).message || 'Invalid username or password.'
    console.error('Login failed:', error)
  }
}
</script>

<template>
  <TooltipProvider class="h-full">
    <div class="flex flex-col gap-6 h-full">
      <div
        class="overflow-hidden py-6 gap-0 h-full rounded-lg"
        :style="{ backgroundImage: `url(/background_pattern_5.png)` }"
      >
        <div class="flex w-full mx-auto flex-col justify-center items-center h-full max-w-xl">
          <div class="p-6 flex items-center justify-center">
            <div>
              <img src="/logo/blue_logo.svg" alt="UNSSC Logo" />
            </div>
          </div>
          <div class="grid p-0 md:grid-cols-1">
            <form class="p-6" @submit.prevent="doLogin">
              <div class="flex flex-col gap-6">
                <div class="flex flex-col text-left text-primary-blue">
                  <h1 class="text-3xl font-black text-center">
                    Welcome to UN Safety & Security Training Database
                  </h1>
                  <p class="mt-8 text-balance text-muted-foreground font-light m">
                    Login to your account
                  </p>
                </div>
                <div class="grid gap-2">
                  <Label for="username">Username</Label>
                  <Input id="username" type="text" placeholder="" required v-model="username" />
                </div>

                <div class="grid gap-2">
                  <div class="flex items-center">
                    <Label for="password">Password</Label>
                    <router-link
                      :to="{ name: 'ForgotPassword' }"
                      class="ml-auto text-sm underline-offset-2 hover:underline"
                    >
                      Forgot your password?
                    </router-link>
                  </div>
                  <div class="relative">
                    <Input
                      id="password"
                      :type="showPassword ? 'text' : 'password'"
                      required
                      v-model="password"
                      class="pr-10"
                    />
                    <Tooltip>
                      <TooltipTrigger as-child>
                        <button
                          type="button"
                          @click="showPassword = !showPassword"
                          class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 cursor-pointer"
                          aria-label="Toggle password visibility"
                        >
                          <Eye v-if="!showPassword" class="h-5 w-5" />
                          <EyeOff v-else class="h-5 w-5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{{ showPassword ? 'Hide Password' : 'Show Password' }}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
                <div v-if="errorMessage" class="text-red-500 text-sm" role="alert">
                  {{ errorMessage }}
                </div>
                <Button
                  type="submit"
                  class="w-full bg-primary-blue hover:scale-[0.99]"
                  bgImage="/background_pattern_2.png"
                >
                  Login
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </TooltipProvider>
</template>
